/** Platform admin app (P3: login with mandatory TOTP, shell, Staff, Tenants list). */
export const admin = {
  login: {
    title: "Sign in · {{product}} Admin",
    heading: "Sign in to {{product}} Admin",
    subtitle: "For CRUD.IT Solutions staff. Two-factor authentication is required.",
    googleSso: "Continue with Google Workspace",
    googleHint: "Google sign-in is limited to @cruditsolutions.com accounts.",
    submit: "Continue",
    tenantConsole: "Looking for your workspace?",
    tenantConsoleLink: "Go to the workspace console",
    totpHeading: "Two-factor verification",
    totpSub: "Enter the 6-digit code from your authenticator app for",
    enrolHeading: "Set up two-factor authentication",
    enrolSub: "2FA is required for all platform staff. Add this account to your authenticator app, then enter the 6-digit code it shows.",
    enrolStep1: "Open Google Authenticator, Microsoft Authenticator, 1Password or Authy.",
    enrolStep2: "Scan this QR code, or enter the setup key manually. On this device, open the setup link instead.",
    enrolStep3: "Enter the 6-digit code to finish.",
    qrLabel: "QR code for setting up two-factor authentication",
    setupKey: "Setup key",
    setupLink: "Open in authenticator app",
    copyKey: "Copy key",
    verify: "Verify & sign in",
    useBackup: "Use a backup code instead",
    useApp: "Use your authenticator app instead",
    backupCodesTitle: "Save your backup codes",
    backupCodesBody: "Each code works once if you lose your phone. They won't be shown again.",
    backupCodesSaved: "I've saved my backup codes",
    copyCodes: "Copy codes",
    ipBlocked: "Sign-in isn't allowed from this network. Use an allowed admin IP or VPN.",
    notStaff: "That account isn't platform staff. Use the workspace console instead.",
    restart: "Start over",
  },
  shell: {
    platformCrumb: "Platform",
    roleNames: {
      super_admin: "Super Admin",
      platform_engineer: "Platform Engineer / Admin",
      sales_billing: "Sales & Billing",
      support_agent: "Support Agent",
      qa: "QA — Read-only",
    },
  },
  staff: {
    title: "Platform staff",
    subtitle: "CRUD.IT Solutions people with access to the Sagot admin console. Separate from tenant team members.",
    accessLog: "Access log",
    invite: "Invite staff",
    policyLead: "2FA is required for all platform staff",
    policyBody: "(TOTP or passkey). Sign-in is limited to @cruditsolutions.com Google Workspace accounts and the allowed admin IPs in",
    policyLink: "Platform settings → Security",
    search: "Search staff",
    count: "{{members}} members · {{pending}} pending invite",
    count_other: "{{members}} members · {{pending}} pending invites",
    colMember: "Member",
    colRole: "Role",
    col2fa: "2FA",
    colLastActive: "Last active",
    colImpersonations: "Impersonations (30d)",
    colActions: "Actions",
    you: "(you)",
    totp: "TOTP",
    setupPending: "Setup pending",
    owner: "Owner",
    roleFor: "Role for {{name}}",
    roleChanged: "{{name}} is now {{role}} · re-auth required",
    ownRole: "You can't change your own role",
    pendingInvite: "Expires {{date}}",
    requiredFirstLogin: "Required on first login",
    never: "Never",
    now: "Now",
    noData: "—",
    emptyTitle: "No staff match",
    emptyBody: "Try a different name or email.",
    loadError: "Staff couldn't load.",
    matrixTitle: "Roles & permissions",
    matrixSubtitle: "What each platform role can do. Super Admin always has every permission. Changes are logged as",
    matrixReadOnly: "Editing the matrix arrives in a later release.",
    permission: "Permission",
    allowed: "Allowed",
    readOnly: "Read-only",
    noAccess: "No access",
    inviteTitle: "Invite platform staff",
    inviteEmail: "Work email",
    inviteEmailPlaceholder: "name@cruditsolutions.com",
    inviteEmailHint: "Only @cruditsolutions.com accounts can be invited.",
    inviteEmailDomain: "Use an @cruditsolutions.com address",
    inviteRole: "Role",
    require2fa: "Require 2FA on first sign-in",
    alwaysOn: "Always on",
    sendInvite: "Send invite",
    cancel: "Cancel",
    invited: "Invite sent · expires in 7 days",
    roleDescriptions: {
      platform_engineer: "Models, keys, system health, impersonation",
      sales_billing: "Tenants, plans, invoices — no keys or impersonation",
      support_agent: "Tickets, read-only impersonation, abuse reports",
      qa: "View everything, change nothing",
      super_admin: "Every permission, including staff management",
    },
    permissions: {
      tenants_view: "View tenants",
      tenants_view_desc: "Directory, usage, health",
      tenants_impersonate: "Impersonate tenants",
      tenants_impersonate_desc: "Always logged; read-only by default",
      billing_manage: "Manage billing",
      billing_manage_desc: "Invoices, credits, refunds (PayMongo)",
      models_manage: "Manage models & keys",
      models_manage_desc: "Providers, routing, API key rotation",
      plans_manage: "Manage plans",
      plans_manage_desc: "Plans, limits, pricing",
      support_manage: "Support tickets & abuse",
      support_manage_desc: "Reply, escalate, suspend agents",
      audit_view: "View audit logs",
      audit_view_desc: "Platform-wide audit trail",
      staff_manage: "Manage staff",
      staff_manage_desc: "Invite, change roles, remove",
      settings_manage: "Platform settings",
      settings_manage_desc: "Branding, security, feature flags",
    },
  },
  tenants: {
    title: "Tenants",
    subtitle: "Every organization on Sagot — schools, clinics, realty, resorts and LGU pilots.",
    search: "Search name or URL…",
    colTenant: "Tenant",
    colIndustry: "Industry",
    colPlan: "Plan",
    colStatus: "Status",
    colAgents: "Agents",
    colMembers: "Members",
    colMessages: "Messages MTD",
    colCreated: "Created",
    noPlan: "No plan",
    trialLeft_one: "Trial — {{count}} day left",
    trialLeft_other: "Trial — {{count}} days left",
    statuses: {
      active: "Active",
      trial: "Trial",
      past_due: "Past due",
      suspended: "Suspended",
      deleted: "Deleted",
    },
    unlimited: "{{used}} / custom",
    usage: "{{used}} / {{limit}}",
    showing_one: "Showing {{count}} tenant",
    showing_other: "Showing {{count}} tenants",
    loadMore: "Load more",
    emptyTitle: "No tenants match these filters",
    emptyBody: "Try a different name or clear the search.",
    clear: "Clear search",
    loadError: "Tenants couldn't load.",
    impersonationEnded: "Support session ended",
  },
} as const;
